<template>
  <div
    class="modal fade"
    id="detailsModal"
    tabindex="-1"
    aria-labelledby="detailsModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content transfer-details-modal">
        <div class="modal-header border-0 pb-0">
          <div>
            <span class="status-badge" :class="`status-${statusValue}`">
              {{ formatStatus(transfer?.status) }}
            </span>
            <h4 class="modal-title text-title mt-3 mb-2" id="detailsModalLabel">
              {{ t("transfer_page.transfer_label") }} {{ transfer?.ref || "" }}
            </h4>
            <p class="text-muted mb-0">
              {{ fromWarehouseName }} {{ t("transfer_page.to") }} {{ toWarehouseName }} • {{ formatDateTime(transfer?.created_at) }}
            </p>
          </div>
          <button
            type="button"
            class="btn-close p-0"
            data-bs-dismiss="modal"
            :aria-label="t('common.close')"
          >
            <img
              src="../../../assets/img/icons/close-circle-2.svg"
              :alt="t('common.close')"
            />
          </button>
        </div>

        <div class="modal-body" v-if="transfer">
          <div class="row g-3 mb-4">
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ t("transfer_page.from_warehouse") }}</span>
                <strong class="summary-value">{{ fromWarehouseName }}</strong>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ t("transfer_page.to_warehouse") }}</span>
                <strong class="summary-value">{{ toWarehouseName }}</strong>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ t("transfer_page.items") }}</span>
                <strong class="summary-value">{{ transfer.items?.length || 0 }}</strong>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ t("transfer_page.grand_total") }}</span>
                <strong class="summary-value">${{ Number(transfer.grand_total || 0).toFixed(2) }}</strong>
              </div>
            </div>
          </div>

          <div class="details-section mb-4">
            <div class="section-header">
              <div>
                <h6 class="section-title mb-1">{{ t("transfer_page.transferred_items") }}</h6>
                <p class="section-subtitle mb-0">{{ t("transfer_page.review_transfer_products") }}</p>
              </div>
            </div>

            <div class="table-responsive transfer-items-table">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{{ t("transfer_page.product") }}</th>
                    <th>{{ t("transfer_page.code") }}</th>
                    <th class="text-center">{{ t("transfer_page.qty") }}</th>
                    <th class="text-end">{{ t("transfer_page.unit_cost") }}</th>
                    <th class="text-end">{{ t("transfer_page.subtotal") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in transfer.items" :key="`${item.product_id}-${index}`">
                    <td>{{ index + 1 }}</td>
                    <td>{{ item.product_name || item.product?.name || t("transfer_page.not_available_short") }}</td>
                    <td>{{ item.product_code || item.product?.code || t("transfer_page.not_available_short") }}</td>
                    <td class="text-center">{{ item.qty }}</td>
                    <td class="text-end">${{ Number(item.unit_cost || 0).toFixed(2) }}</td>
                    <td class="text-end">${{ (Number(item.unit_cost || 0) * Number(item.qty || 0)).toFixed(2) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="details-section">
            <div class="row g-3">
              <div class="col-md-8">
                <h6 class="section-title mb-2">{{ t("transfer_page.notes") }}</h6>
                <p class="note-block mb-0">{{ transfer.note || t("transfer_page.no_notes_added") }}</p>
              </div>
              <div class="col-md-4">
                <div class="totals-card">
                  <div class="total-row">
                    <span>{{ t("transfer_page.tax") }}</span>
                    <strong>${{ Number(transfer.total_tax || 0).toFixed(2) }}</strong>
                  </div>
                  <div class="total-row">
                    <span>{{ t("transfer_page.shipping") }}</span>
                    <strong>${{ Number(transfer.shipping || 0).toFixed(2) }}</strong>
                  </div>
                  <div class="total-row grand">
                    <span>{{ t("transfer_page.grand_total") }}</span>
                    <strong>${{ Number(transfer.grand_total || 0).toFixed(2) }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button
            type="button"
            class="btn style-five upload-btn px-xxl-6"
            @click="printTransfer"
          >
            {{ t("transfer_page.print") }}
            <img src="../../../assets/img/icons/download.svg" :alt="t('transfer_page.print')" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";
import type { Transfer } from "../../../services/transfer";

export default defineComponent({
  name: "DetailsModal",
  props: {
    transfer: {
      type: Object as PropType<Transfer | null>,
      default: null,
    },
  },
  setup(props) {
    const { t, locale } = useI18n();
    const fromWarehouseName = computed(
      () => props.transfer?.from_warehouse?.name || props.transfer?.from_wh_name || t("transfer_page.not_available_short")
    );
    const toWarehouseName = computed(
      () => props.transfer?.to_warehouse?.name || props.transfer?.to_wh_name || t("transfer_page.not_available_short")
    );
    const statusValue = computed(() => String(props.transfer?.status || "pending").toLowerCase());

    const formatStatus = (status?: string) => {
      const value = String(status || "pending").toLowerCase();
      return t(`transfer_page.status_${value}`);
    };

    const formatDateTime = (dateString?: string) => {
      if (!dateString) return t("transfer_page.not_available_short");
      return new Date(dateString).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const printTransfer = () => {
      if (!props.transfer) {
        Swal.fire({
          icon: "error",
          title: t("transfer_page.error"),
          text: t("transfer_page.no_transfer_selected_to_print"),
        });
        return;
      }

      const rows = (props.transfer.items || [])
        .map(
          (item, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${item.product_name || item.product?.name || t("transfer_page.not_available_short")}</td>
              <td>${item.product_code || item.product?.code || t("transfer_page.not_available_short")}</td>
              <td style="text-align:center;">${item.qty}</td>
              <td style="text-align:right;">$${Number(item.unit_cost || 0).toFixed(2)}</td>
              <td style="text-align:right;">$${(Number(item.unit_cost || 0) * Number(item.qty || 0)).toFixed(2)}</td>
            </tr>
          `
        )
        .join("");

      const printMarkup = `
        <html>
          <head>
            <title>${t("transfer_page.transfer_label")} ${props.transfer.ref}</title>
            <style>
              @page { size: auto; margin: 14mm; }
              body { font-family: Arial, sans-serif; padding: 32px; color: #1f2940; }
              .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
              .status { display: inline-block; padding: 6px 12px; border-radius: 999px; background: #e8f7ee; color: #1f9254; font-size: 12px; font-weight: 700; }
              h1 { margin: 12px 0 6px; font-size: 30px; }
              .muted { color: #6b7280; font-size: 14px; }
              .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }
              .card { border: 1px solid #e6ebf2; border-radius: 12px; padding: 16px; }
              .label { display: block; color: #6b7280; font-size: 12px; margin-bottom: 8px; }
              .value { font-size: 18px; font-weight: 700; }
              table { width: 100%; border-collapse: collapse; margin-top: 8px; }
              th, td { border-bottom: 1px solid #e6ebf2; padding: 12px 10px; font-size: 14px; }
              th { text-align: left; background: #f8fafc; }
              .totals { margin-top: 24px; width: 320px; margin-left: auto; }
              .total-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e6ebf2; }
              .grand { font-size: 18px; font-weight: 700; }
            </style>
          </head>
          <body>
            <div class="header">
              <div>
                <span class="status">${formatStatus(props.transfer.status)}</span>
                <h1>${t("transfer_page.transfer_label")} ${props.transfer.ref}</h1>
                <div class="muted">${fromWarehouseName.value} ${t("transfer_page.to")} ${toWarehouseName.value}</div>
                <div class="muted">${formatDateTime(props.transfer.created_at)}</div>
              </div>
            </div>

            <div class="summary">
              <div class="card"><span class="label">${t("transfer_page.from_warehouse")}</span><div class="value">${fromWarehouseName.value}</div></div>
              <div class="card"><span class="label">${t("transfer_page.to_warehouse")}</span><div class="value">${toWarehouseName.value}</div></div>
              <div class="card"><span class="label">${t("transfer_page.items")}</span><div class="value">${props.transfer.items?.length || 0}</div></div>
              <div class="card"><span class="label">${t("transfer_page.grand_total")}</span><div class="value">$${Number(props.transfer.grand_total || 0).toFixed(2)}</div></div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>${t("transfer_page.product")}</th>
                  <th>${t("transfer_page.code")}</th>
                  <th style="text-align:center;">${t("transfer_page.qty")}</th>
                  <th style="text-align:right;">${t("transfer_page.unit_cost")}</th>
                  <th style="text-align:right;">${t("transfer_page.subtotal")}</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>

            <div class="totals">
              <div class="total-row"><span>${t("transfer_page.tax")}</span><strong>$${Number(props.transfer.total_tax || 0).toFixed(2)}</strong></div>
              <div class="total-row"><span>${t("transfer_page.shipping")}</span><strong>$${Number(props.transfer.shipping || 0).toFixed(2)}</strong></div>
              <div class="total-row grand"><span>${t("transfer_page.grand_total")}</span><strong>$${Number(props.transfer.grand_total || 0).toFixed(2)}</strong></div>
            </div>
          </body>
        </html>
      `;

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");
      document.body.appendChild(iframe);

      const iframeWindow = iframe.contentWindow;
      const iframeDocument = iframe.contentDocument || iframeWindow?.document;

      if (!iframeWindow || !iframeDocument) {
        document.body.removeChild(iframe);
        Swal.fire({
          icon: "error",
          title: t("transfer_page.print_failed"),
          text: t("transfer_page.unable_to_prepare_transfer_for_printing"),
        });
        return;
      }

      iframeDocument.open();
      iframeDocument.write(printMarkup);
      iframeDocument.close();

      let hasPrinted = false;
      const cleanup = () => {
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 300);
      };

      const triggerPrint = () => {
        if (hasPrinted) return;
        hasPrinted = true;
        iframeWindow.focus();
        iframeWindow.print();
        iframeWindow.onafterprint = cleanup;
      };

      iframe.onload = () => {
        setTimeout(triggerPrint, 250);
      };

      setTimeout(() => {
        try {
          triggerPrint();
        } catch (error) {
          console.error("Transfer print fallback failed:", error);
          cleanup();
        }
      }, 600);
    };

    return {
      fromWarehouseName,
      toWarehouseName,
      statusValue,
      formatStatus,
      formatDateTime,
      printTransfer,
      t,
    };
  },
});
</script>

<style scoped>
.transfer-details-modal {
  border: 0;
  border-radius: 20px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 92px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-completed {
  color: #1f9254;
  background: #e8f7ee;
}

.status-pending {
  color: #d33c43;
  background: #fdecec;
}

.status-sent {
  color: #3563e9;
  background: #edf4ff;
}

.summary-card,
.details-section,
.totals-card {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 16px;
}

.summary-card {
  padding: 18px;
}

.summary-label {
  display: block;
  margin-bottom: 10px;
  color: #7f8aa3;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.summary-value {
  color: #1f2940;
  font-size: 18px;
}

.details-section {
  padding: 20px;
}

.section-title {
  color: #1f2940;
  font-size: 20px;
  font-weight: 700;
}

.section-subtitle {
  color: #7f8aa3;
  font-size: 14px;
}

.transfer-items-table thead th {
  border-bottom: 1px solid #e8edf5;
  color: #1f2940;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.transfer-items-table tbody td {
  color: #4b5565;
  padding-top: 14px;
  padding-bottom: 14px;
}

.note-block {
  min-height: 120px;
  border: 1px dashed #d7deea;
  border-radius: 14px;
  padding: 16px;
  color: #4b5565;
  background: #f8fbff;
}

.totals-card {
  padding: 16px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  color: #4b5565;
}

.total-row + .total-row {
  border-top: 1px solid #edf2f7;
}

.total-row.grand {
  color: #1f2940;
  font-size: 18px;
}
</style>
